import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useModals } from "@mantine/modals";
import { useState, useEffect } from "react";
import { BsCreditCard } from "react-icons/bs";
import { MdClose } from "react-icons/md";
import { useAppContext } from "src/api/AppContext";
import { ISelectedPayment } from "src/components/PaymentSelection";
import {
  DraftMode,
  useGetSignedAgreementOnPaymentQuery,
  useUpdateSignedAgreementMutation,
} from "src/generated/graphql";
import { currencyFormatter } from "src/utils/formatting";

interface AgreementOverviewProps {
  clientId: string;
  selectedAgreementId: string;
  paymentMethod: ISelectedPayment;
  handleModalClose: () => void;
}

const Confirmation = ({
  clientId,
  selectedAgreementId,
  paymentMethod,
  handleModalClose,
}: AgreementOverviewProps) => {
  const theme = useMantineTheme();
  const { currentLocation } = useAppContext();
  const [dueNow, setDueNow] = useState<number>(0);

  const { data: signedAgreement } = useGetSignedAgreementOnPaymentQuery({
    variables: {
      agreementId: selectedAgreementId,
      clientId,
      locationId: currentLocation?.id || "",
    },
    skip: !currentLocation?.id,
  });

  const [updateAgreement, {}] = useUpdateSignedAgreementMutation({
    onCompleted: () => {
      handleModalClose();
    },
  });

  const { agreement, id: signedAgreementId } =
    signedAgreement?.checkForSignedAgreement || {};

  const confirmAgreement = async () => {
    if (signedAgreementId) {
      updateAgreement({
        variables: {
          data: {
            paymentMethod: paymentMethod ? { id: paymentMethod.id } : undefined,
          },
          id: signedAgreementId,
        },
        fetchPolicy: "no-cache",
      });
    } else {
      handleModalClose();
    }
  };

  useEffect(() => {
    if (agreement) {
      let initalChargeTotal = agreement?.initialCharges
        .map((charge) => charge.cost)
        .reduce((a, b) => a + b, 0);
      let recurringChargeToday = 0;
      if (agreement.draftMode == DraftMode.Immediate) {
        recurringChargeToday = agreement?.recurringCharges
          .map((charge) => charge.cost)
          .reduce((a, b) => a + b, 0);
      }

      setDueNow(initalChargeTotal + recurringChargeToday);
    }
  }, [agreement]);

  return (
    <div
      style={{
        width: "100%",
        minHeight: 500,
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
      }}
    >
      <Flex
        align={"center"}
        justify={"space-between"}
        style={{
          padding: "10px 20px",
          marginBottom: "10px",
          borderBottom: `2px solid ${theme.colors.light[5]}`,
        }}
      >
        <Text size={22} fw={500}>
          Agreement Confirmation
        </Text>
        <ActionIcon size="lg" onClick={handleModalClose}>
          <MdClose size={20} />
        </ActionIcon>
      </Flex>

      <Flex direction={"column"} align={"center"} justify={"center"} mx={20}>
        <Box w={120} h={120} bg={theme.colors.light[2]}></Box>
        <Text my={15} size={16} fw={500}>
          {agreement?.name}
        </Text>
        <Text align="center">{agreement?.description}</Text>
      </Flex>
      <Flex direction={"column"} mx={20}>
        <Text my={15} size={16} fw={500}>
          Payment
        </Text>
        <Flex
          w={"100%"}
          p={10}
          align={"center"}
          style={{
            borderRadius: 5,
            background: theme.colors.secondary[6],
          }}
        >
          <Flex align={"center"}>
            <Box
              w={40}
              h={40}
              style={{
                borderRadius: 5,
                background: theme.colors.secondary[4],
              }}
            >
              <Flex w={"100%"} h={"100%"} align={"center"} justify={"center"}>
                <BsCreditCard color="white" size={25} />
              </Flex>
            </Box>
            <Flex direction={"column"} mx={10}>
              <Text
                size={16}
                fw={500}
              >{`${paymentMethod?.name} - ${paymentMethod?.mask}`}</Text>
            </Flex>
          </Flex>
        </Flex>
        <Text my={15} size={16} fw={500}>
          {`Due Today: ${currencyFormatter.format(dueNow)}`}
        </Text>
      </Flex>

      <Flex justify={"space-between"} align={"center"} mt={25}>
        <Button
          radius="lg"
          variant="filled"
          onClick={() => confirmAgreement()}
          w={"100%"}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          Confirm and Pay
        </Button>
      </Flex>
    </div>
  );
};

export default Confirmation;
